import { mainApi } from '../../../../../api/mainApi';
import { middlewareApi } from '../../../../middleware/middlewareApi';
import { StudentAsigned, StudentsAsignedResponse } from '../interfaces/students-asigned.interfaces';
import { StudentCourse, StudentCourseDetail } from '../interfaces/student-course.interfaces';
import {
  StudentAnnouncement,
  StudentAnnouncements,
  UserAnnouncements,
} from '../interfaces/student-announcement.interfaces';
import {
  setStudentList,
  setStudentCourseDetail,
  setUserHistoryAnnouncements,
  setStudentHistoryAnouncements,
  resetStudentHistoryAnouncements,
} from './studentsAsignedSlice';
import { CollaboratorByUserID } from '../interfaces/collaboratorByUserId.interfaces';

export const studentAsignedTags = middlewareApi.enhanceEndpoints({
  addTagTypes: ['StudentsAsignedTag', 'StudentAnnouncementsHistoryPreview'],
});
// Define a service using a base URL and expected endpoints
export const studentsAsignedApi = studentAsignedTags.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * @GET all students asigned teacher
     */
    getStudentsAsigned: builder.query<
      StudentsAsignedResponse,
      {
        profileId: number;
        page: number;
        perPage: number;
        search: string;
        education_level_id?: number;
        offer_id?: number;
        period_id?: number;
        collaborator_id?: number;
      }
    >({
      queryFn: async (
        {
          profileId,
          page,
          search,
          perPage,
          education_level_id,
          offer_id,
          collaborator_id,
          period_id,
        },
        { dispatch }
      ) => {
        try {
          const { data } = await mainApi.get<StudentsAsignedResponse>('/collaborators/students', {
            params: {
              user_profile_id: profileId,
              page,
              size: perPage,
              type_sort: 'desc',
              search,
              education_level_id,
              offer_id,
              period_id,
              collaborator_id,
            },
          });

          dispatch(setStudentList(data?.data!));

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(
                ({ user }) => ({ type: 'StudentsAsignedTag', id: user.user_id } as const)
              ),
              { type: 'StudentsAsignedTag', id: 'LIST' },
            ]
          : [{ type: 'StudentsAsignedTag', id: 'LIST' }],
    }),

    getStudentAssigned: builder.query<
      StudentAsigned,
      {
        profileId: number;
        studentId: number;
        collaboratorId: number;
      }
    >({
      queryFn: async ({ profileId, studentId, collaboratorId }, { dispatch }) => {
        try {
          const { data } = await mainApi.get<StudentAsigned[][]>(`collaborators/get-one-student`, {
            params: {
              user_profile_id: profileId,
              student_id: studentId,
              collaborator_id: collaboratorId,
            },
          });

          const student = data?.[0]?.[0];

          return { data: student };
        } catch (error: any) {
          return { error };
        }
      },
    }),

    /**
     * @GET courses student
     */
    getCoursesStudent: builder.query<StudentCourse[], { studentId: number }>({
      queryFn: async ({ studentId }, { dispatch }) => {
        try {
          const { data } = await mainApi.get<StudentCourse[]>(`/students/${studentId}/courses`);

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
    }),

    /**
     * @GET get student course
     */
    getStudentCourse: builder.query<StudentCourseDetail[], { courseId: number; studentId: number }>(
      {
        queryFn: async ({ courseId, studentId }, { dispatch }) => {
          try {
            const { data } = await mainApi.get<StudentCourseDetail[]>(
              `/courses/${courseId}/students/${studentId}/scores`
            );

            dispatch(setStudentCourseDetail(data));

            return { data: Array.isArray(data) ? data : [] };
          } catch (error: any) {
            return { error };
          }
        },
      }
    ),

    /**
     * @GET Obtain announcements user logged
     */
    getUserAnnouncements: builder.query<
      UserAnnouncements[],
      { user_profile_id: number; offset?: number; limit?: number; acronym?: string }
    >({
      queryFn: async (
        { user_profile_id, offset, limit, acronym = 'CONS' },
        { dispatch, getState }
      ) => {
        try {
          const { data } = await mainApi.get<UserAnnouncements[]>('/users/announcements', {
            params: {
              user_profile_id,
              offset,
              limit,
              acronym,
            },
          });

          dispatch(setUserHistoryAnnouncements(data));

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
    }),

    /**
     * @GET Obtain announcements user logged preview
     */
    getUserAnnouncementsPreview: builder.query<
      UserAnnouncements[],
      { user_profile_id: number; offset?: number }
    >({
      queryFn: async ({ user_profile_id, offset }, { dispatch, getState }) => {
        try {
          const { data } = await mainApi.get<UserAnnouncements[]>('/users/announcements', {
            params: {
              user_profile_id,
              offset,
              limit: 6,
              acronym: 'CONS',
            },
          });

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
    }),

    /**
     * @GET Obtain announcements student
     */
    getStudentAnnouncementsHistory: builder.query<
      StudentAnnouncements[],
      { studentId: number; user_profile_id: number; offset?: number; limit?: number }
    >({
      queryFn: async ({ studentId, user_profile_id, offset, limit }, { dispatch }) => {
        try {
          const { data } = await mainApi.get<StudentAnnouncements[]>(
            `/users/${studentId}/announcements`,
            {
              params: {
                user_profile_id,
                offset,
                limit,
                acronym: 'CONS',
              },
            }
          );

          if (offset === 0) dispatch(resetStudentHistoryAnouncements());
          dispatch(setStudentHistoryAnouncements(data));

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
    }),

    getStudentAnnouncementsHistoryPreview: builder.query<
      StudentAnnouncements[],
      { studentId: number; user_profile_id: number; offset?: number }
    >({
      queryFn: async ({ studentId, user_profile_id, offset }, { dispatch }) => {
        try {
          const { data } = await mainApi.get<StudentAnnouncements[]>(
            `/users/${studentId}/announcements`,
            {
              params: {
                user_profile_id,
                offset,
                limit: 6,
                acronym: 'CONS',
              },
            }
          );

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: [{ type: 'StudentAnnouncementsHistoryPreview', id: 'LIST' }],
    }),

    /**
     * @POST Add students announcements
     */
    addStudentAnnouncement: builder.mutation<
      StudentAnnouncement,
      { profileId: number; studentAdsPayload: StudentAnnouncement }
    >({
      queryFn: async ({ profileId, studentAdsPayload }) => {
        try {
          const { data } = await mainApi.post<StudentAnnouncement>('/users/ads', {
            ...studentAdsPayload,
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [
        { type: 'StudentsAsignedTag', id: 'LIST' },
        {
          type: 'StudentAnnouncementsHistoryPreview',
          id: 'LIST',
        },
      ],
    }),

    getCollaboratorByStudents: builder.query<CollaboratorByUserID, { user_id: number | string }>({
      queryFn: async ({ user_id }, { dispatch }) => {
        try {
          const { data } = await mainApi.get<CollaboratorByUserID>(
            `students/get-collaborator-by-student/${user_id}`
          );

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetStudentsAsignedQuery,
  useGetStudentCourseQuery,
  useGetUserAnnouncementsQuery,
  useGetCoursesStudentQuery,
  useGetUserAnnouncementsPreviewQuery,
  useGetStudentAnnouncementsHistoryQuery,
  useGetStudentAnnouncementsHistoryPreviewQuery,
  useAddStudentAnnouncementMutation,
  useLazyGetStudentAssignedQuery,
  useGetCollaboratorByStudentsQuery,
} = studentsAsignedApi;
